import { createContext, useContext, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendEmailVerification,
  onAuthStateChanged,
  getAuth,
  signOut,
} from "firebase/auth";
import {
  updateDoc,
  doc,
  getDoc,
  setDoc,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../Firebase/firebase";

const BusinessContext = createContext(null);

export const BusinessProvider = ({ children }) => {
  const auth = getAuth();

  const [user, setUser] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(true);

  // ===============================
  // 🔐 AUTH + PROFILE LISTENER
  // ===============================
  useEffect(() => {
    let unsubscribeProfile = null;

    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);

      if (!currentUser) {
        setCompanyProfile(null);
        setLoading(false);
        setProfileLoading(false);
        return;
      }

      setProfileLoading(true);

      const userRef = doc(db, "supplier", currentUser.uid);

      unsubscribeProfile = onSnapshot(
        userRef,
        (snapshot) => {
          if (snapshot.exists()) {
            const data = snapshot.data();

            // Se quiser bloquear status pendente
            if (data?.status === "pendente") {
              setCompanyProfile(null);
            }
            else if(data?.status === "Pending"){

              setCompanyProfile(null);
              alert('Aguardando Pela Tou AKI MANAGEMENT Aprovar')
            }
            else {
              setCompanyProfile({
                id: snapshot.id,
                ...data,
              });
            }
          } else {
            setCompanyProfile(null);
          }

          setLoading(false);
          setProfileLoading(false);
        },
        (error) => {
          console.error("Erro ao ouvir perfil:", error);
          setCompanyProfile(null);
          setLoading(false);
          setProfileLoading(false);
        }
      );
    });

    return () => {
      if (unsubscribeProfile) unsubscribeProfile();
      unsubscribeAuth();
    };
  }, [auth]);

  // ===============================
  // 📝 REGISTER
  // ===============================
  const register = async (data) => {
    const {
      nome,
      nif,
      email,
      whatsapp,
      nomegerente,
      pais,
      localidade,
      tipoNegocio,
      pagamento,
      acordo,
      password,
      confirmPassword,
      parceiro,
    
    } = data;

    if (!password || password !== confirmPassword) {
      throw new Error("As senhas não coincidem");
    }

    // 🔒 Evita NIF duplicado
    const idRef = doc(db, "idCards", nif);
    const idSnap = await getDoc(idRef);

    if (idSnap.exists()) {
      throw new Error("Este NIF já foi utilizado.");
    }

    // Criar conta
    const cred = await createUserWithEmailAndPassword(auth, email, password);

    // Enviar verificação
    await sendEmailVerification(cred.user);

    // Criar perfil
    await setDoc(doc(db, "supplier", cred.user.uid), {
      nome,
      nif,
      email,
      whatsapp,
      nomegerente,
      pais,
      localidade,
      tipoNegocio,
      pagamento,
      acordo,
      parceiro,
      status: "Pending",
      createdAt: new Date(),
    });

    // Registar NIF
    await setDoc(idRef, {
      uid: cred.user.uid,
      whatsapp,
      createdAt: new Date(),
    });

    return cred.user;
  };

  // ===============================
  // 🔑 LOGIN
  // ===============================
  const login = async (email, password) => {
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);

      if (!cred.user.emailVerified) {
        await signOut(auth);
        throw new Error("Verifique o seu e-mail antes de entrar.");
      }

      return cred.user;
    } catch (err) {
      console.error("Erro no login:", err.message);
      throw new Error("Credenciais inválidas ou email não verificado.");
    }
  };

  // ===============================
  // 🔄 UPDATE PROFILE
  // ===============================
  const updateUserProfile = async (uid, data) => {
    if (!uid) throw new Error("UID inválido");

    const userRef = doc(db, "supplier", uid);

    await updateDoc(userRef, {
      ...data,
      updatedAt: new Date(),
    });
  };

  // ===============================
  // 🚪 LOGOUT
  // ===============================
  const logout = async () => {
    await signOut(auth);
  };

  return (
    <BusinessContext.Provider
      value={{
        user,
        companyProfile,
        register,
        login,
        logout,
        updateUserProfile,
        loading,
        profileLoading,
      }}
    >
      {!loading && children}
    </BusinessContext.Provider>
  );
};

// Hook personalizado
export const useBusinessAuth = () => {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error("useBusinessAuth deve ser usado dentro de BusinessProvider");
  }
  return context;
};